import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';

const MultiStoreComparison = ({ data, loading }) => {
  const [selectedMetric, setSelectedMetric] = useState('totalCost');
  const [comparisonType, setComparisonType] = useState('absolute');

  const mockData = [
    {
      store: 'Dhaka - Gulshan',
      totalCost: 1250000,
      installations: 45,
      avgCostPerInstall: 27778,
      roi: 8.5,
      completionRate: 95,
      brands: 6
    },
    {
      store: 'Dhaka - Dhanmondi',
      totalCost: 980000,
      installations: 38,
      avgCostPerInstall: 25789,
      roi: 7.2,
      completionRate: 92,
      brands: 5
    },
    {
      store: 'Chittagong - Agrabad',
      totalCost: 850000,
      installations: 32,
      avgCostPerInstall: 26563,
      roi: 6.8,
      completionRate: 89,
      brands: 4
    },
    {
      store: 'Sylhet - Zindabazar',
      totalCost: 720000,
      installations: 28,
      avgCostPerInstall: 25714,
      roi: 9.2,
      completionRate: 85,
      brands: 4
    },
    {
      store: 'Rajshahi - Shaheb Bazar',
      totalCost: 590000,
      installations: 25,
      avgCostPerInstall: 23600,
      roi: 5.4,
      completionRate: 88,
      brands: 3
    }
  ];

  const chartData = data || mockData;

  const metricOptions = [
    { value: 'totalCost', label: 'Total Cost' },
    { value: 'installations', label: 'Installations' },
    { value: 'avgCostPerInstall', label: 'Avg Cost per Install' },
    { value: 'roi', label: 'ROI (%)' },
    { value: 'completionRate', label: 'Completion Rate (%)' },
    { value: 'brands', label: 'Active Brands' }
  ];

  const comparisonOptions = [
    { value: 'absolute', label: 'Absolute Values' },
    { value: 'percentage', label: 'Percentage Difference' }
  ];

  const getMetricLabel = (metric) => {
    const option = metricOptions?.find(opt => opt?.value === metric);
    return option ? option?.label : metric;
  };

  const formatValue = (value, metric) => {
    switch (metric) {
      case 'totalCost': case'avgCostPerInstall':
        return `৳${(value / 1000)?.toFixed(0)}K`;
      case 'roi': case'completionRate':
        return `${value}%`;
      default:
        return value?.toString();
    }
  };

  const getProcessedData = () => {
    if (comparisonType === 'absolute') {
      return chartData;
    }

    // Calculate percentage difference from average
    const avgValue = chartData?.reduce((sum, item) => sum + item?.[selectedMetric], 0) / chartData?.length;
    
    return chartData?.map(item => ({
      ...item,
      [selectedMetric]: ((item?.[selectedMetric] - avgValue) / avgValue) * 100
    }));
  };

  const processedData = getProcessedData();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-surface border border-border rounded-lg p-4 shadow-elevated min-w-64">
          <p className="font-semibold text-foreground mb-3">{label}</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground text-sm">{getMetricLabel(selectedMetric)}:</span>
              <span className="font-medium text-foreground">
                {comparisonType === 'percentage' 
                  ? `${payload?.[0]?.value?.toFixed(1)}%`
                  : formatValue(payload?.[0]?.value, selectedMetric)
                }
              </span>
            </div>
            {comparisonType === 'absolute' && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">Total Cost:</span>
                  <span className="font-medium text-foreground">{formatValue(data?.totalCost, 'totalCost')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">Installations:</span>
                  <span className="font-medium text-foreground">{data?.installations}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">ROI:</span>
                  <span className="font-medium text-success">{data?.roi}%</span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const getBarColor = (value) => {
    if (comparisonType === 'percentage') {
      return value >= 0 ? 'var(--color-success)' : 'var(--color-error)';
    }
    return 'var(--color-primary)';
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-80">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} />
            </div>
            <span>Loading comparison data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Multi-Store Comparison</h3>
          <p className="text-sm text-muted-foreground">Compare performance metrics across store locations</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Select
            options={metricOptions}
            value={selectedMetric}
            onChange={setSelectedMetric}
            className="w-full sm:w-48"
          />
          <Select
            options={comparisonOptions}
            value={comparisonType}
            onChange={setComparisonType}
            className="w-full sm:w-48"
          />
        </div>
      </div>
      <div className="h-80 w-full mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="store" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => 
                comparisonType === 'percentage' 
                  ? `${value?.toFixed(0)}%`
                  : selectedMetric?.includes('Cost') 
                    ? `৳${(value / 1000)?.toFixed(0)}K`
                    : value?.toString()
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey={selectedMetric}
              fill={(entry) => getBarColor(entry)}
              radius={[4, 4, 0, 0]}
              name={getMetricLabel(selectedMetric)}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Performance Indicators */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-sm font-medium text-foreground">Best Performer</span>
          </div>
          <div className="text-lg font-semibold text-foreground">
            {processedData?.reduce((best, current) => 
              current?.[selectedMetric] > best?.[selectedMetric] ? current : best
            )?.store}
          </div>
          <div className="text-sm text-muted-foreground">
            {formatValue(
              processedData?.reduce((best, current) => 
                current?.[selectedMetric] > best?.[selectedMetric] ? current : best
              )?.[selectedMetric], 
              selectedMetric
            )}
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BarChart3" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Average</span>
          </div>
          <div className="text-lg font-semibold text-foreground">
            {formatValue(
              chartData?.reduce((sum, item) => sum + item?.[selectedMetric], 0) / chartData?.length,
              selectedMetric
            )}
          </div>
          <div className="text-sm text-muted-foreground">Across all stores</div>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" size={16} className="text-warning" />
            <span className="text-sm font-medium text-foreground">Improvement Needed</span>
          </div>
          <div className="text-lg font-semibold text-foreground">
            {processedData?.reduce((worst, current) => 
              current?.[selectedMetric] < worst?.[selectedMetric] ? current : worst
            )?.store}
          </div>
          <div className="text-sm text-muted-foreground">
            {formatValue(
              processedData?.reduce((worst, current) => 
                current?.[selectedMetric] < worst?.[selectedMetric] ? current : worst
              )?.[selectedMetric], 
              selectedMetric
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiStoreComparison;